// ./src/utils/expenseHelpers.ts
// Helper functions for expense-related data processing

import { Expense } from "@/utils/api";

// ============================================
// Types
// ============================================

/**
 * Minimal interface for records with odometer readings.
 * Allows the helper to work with any record type that has these fields.
 */
export interface OdometerRecord {
  id?: string;
  odometer?: number | null;
  whenDone?: string | null;
  car?: {
    id?: string;
  } | null;
  carId?: string | null;
}

/**
 * Minimal interface for records with weather data.
 */
export interface WeatherRecord {
  weatherTempC?: number | null;
}

// ============================================
// Odometer Helpers
// ============================================

/**
 * Detect odometer anomalies - readings that are lower than a previous reading
 * for the same car. This helps identify potential data entry errors.
 *
 * Algorithm:
 * 1. Groups records by car ID
 * 2. Sorts each car's records by date (oldest first)
 * 3. Tracks the maximum odometer seen as it iterates
 * 4. Any reading less than the previous maximum gets flagged as an anomaly
 *
 * @param records - Array of records with odometer data
 * @returns Set of record IDs that have anomalies (odometer went down)
 *
 * @example
 * ```ts
 * const anomalies = detectOdometerAnomalies(checkpoints);
 * const hasAnomaly = anomalies.has(checkpoint.id);
 * ```
 */
export const detectOdometerAnomalies = <T extends OdometerRecord>(
  records: T[]
): Set<string> => {
  const anomalyIds = new Set<string>();

  // Group records by car ID
  const byCarId = new Map<string, T[]>();

  for (const record of records) {
    // Get car ID from either car.id or carId
    const carId = record.car?.id || record.carId;

    // Skip if no car, no ID, or no odometer reading
    if (
      !carId ||
      !record.id ||
      record.odometer === null ||
      record.odometer === undefined
    ) {
      continue;
    }

    if (!byCarId.has(carId)) {
      byCarId.set(carId, []);
    }
    byCarId.get(carId)!.push(record);
  }

  // For each car, sort by date and find anomalies
  for (const [, carRecords] of byCarId) {
    // Need at least 2 records to detect anomalies
    if (carRecords.length < 2) {
      continue;
    }

    // Sort by date ascending (oldest first)
    const sorted = [...carRecords].sort((a, b) => {
      const dateA = new Date(a.whenDone || 0).getTime();
      const dateB = new Date(b.whenDone || 0).getTime();
      return dateA - dateB;
    });

    // Track the maximum odometer seen so far
    let maxOdometer = -Infinity;

    for (const record of sorted) {
      const odometer = record.odometer!;

      if (odometer < maxOdometer) {
        // This is an anomaly - odometer went down compared to a previous reading
        anomalyIds.add(record.id!);
      } else {
        // Update max only if this reading is valid (not an anomaly)
        maxOdometer = Math.max(maxOdometer, odometer);
      }
    }
  }

  return anomalyIds;
};

/**
 * Check if two odometer readings are consistent (second is greater than or equal to first).
 * Useful for form validation.
 *
 * @param earlier - The earlier odometer reading
 * @param later - The later odometer reading
 * @returns true if readings are consistent, false if there's an anomaly
 */
export const isOdometerConsistent = (
  earlier: number | null | undefined,
  later: number | null | undefined
): boolean => {
  // If either is missing, we can't determine consistency
  if (
    earlier === null ||
    earlier === undefined ||
    later === null ||
    later === undefined
  ) {
    return true;
  }

  return later >= earlier;
};

// ============================================
// Weather Helpers
// ============================================

/**
 * Check if a record has weather data available.
 *
 * @param record - Record to check for weather data
 * @returns true if weather data is present
 *
 * @example
 * ```ts
 * if (hasWeatherData(expense)) {
 *   // Show weather display
 * }
 * ```
 */
export const hasWeatherData = <T extends WeatherRecord>(record: T): boolean => {
  return record.weatherTempC !== null && record.weatherTempC !== undefined;
};

/**
 * Check if an Expense record has weather data.
 * Type-safe version specifically for Expense type.
 *
 * @param expense - Expense record to check
 * @returns true if weather data is present
 */
export const expenseHasWeatherData = (expense: Expense): boolean => {
  return hasWeatherData(expense);
};